/**
 * Today Schedule Gantt Chart Component
 * 本日のクラススケジュールをガントチャート形式で表示（ダークモード対応）
 */

import { Card, Stack, Text, Group, Badge, Tooltip, useMantineColorScheme } from '@mantine/core';
import { motion } from 'framer-motion';
import { getGlassStyle } from '../styles/darkModeGlass';
import type { TodayScheduleClass } from '../types/enhanced';

const MotionCard = motion(Card);

export const TodayScheduleGantt = ({ schedule }: { schedule: TodayScheduleClass[] }) => {
  const { colorScheme } = useMantineColorScheme();

  // 時間を分に変換（10:00 = 0分起点）
  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours - 10) * 60 + minutes; // 10:00を0分とする
  };

  const totalMinutes = 11 * 60; // 10:00-21:00 = 11時間

  return (
    <MotionCard
      padding="sm"
      radius="sm"
      shadow="sm"
      style={{
        ...getGlassStyle(colorScheme),
        minHeight: '240px',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <Group justify="space-between" mb="md">
        <Text size="lg" fw={600}>
          本日のクラススケジュール
        </Text>
        <Text size="sm" c="dimmed">
          {schedule.length}クラス
        </Text>
      </Group>

      {/* タイムライン */}
      <Group mb="md" gap={0} style={{ position: 'relative', paddingBottom: 8, borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
        {[10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((hour) => (
          <div key={hour} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            <Text size="xs" c="dimmed" fw={500}>
              {hour}
            </Text>
          </div>
        ))}
      </Group>

      {/* 縦線のグリッド */}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
          {[10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((hour, index) => (
            <div
              key={hour}
              style={{
                position: 'absolute',
                left: `${(index / 11) * 100}%`,
                top: 0,
                bottom: 0,
                width: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              }}
            />
          ))}
        </div>

      <Stack gap="md" style={{ position: 'relative' }}>
        {schedule.map((cls, index) => {
          const startMinutes = timeToMinutes(cls.start_time);
          const endMinutes = timeToMinutes(cls.end_time);
          const duration = endMinutes - startMinutes;
          const leftPercent = (startMinutes / totalMinutes) * 100;
          const widthPercent = (duration / totalMinutes) * 100;

          // 出席率に応じて色を変更
          const gradientColor =
            cls.attendance_rate >= 80
              ? 'linear-gradient(135deg, #51cf66, #40c057)' // 緑
              : cls.attendance_rate >= 60
              ? 'linear-gradient(135deg, #339af0, #228be6)' // 青
              : 'linear-gradient(135deg, #ffd43b, #fab005)'; // 黄

          return (
            <motion.div
              key={cls.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Tooltip
                label={
                  <div>
                    <Text size="sm" fw={600}>
                      {cls.class_name}
                    </Text>
                    <Text size="xs">講師: {cls.instructor_name}</Text>
                    <Text size="xs">
                      登録者: {cls.enrolled}/{cls.capacity}名
                    </Text>
                    <Text size="xs">出席率: {cls.attendance_rate}%</Text>
                  </div>
                }
                withArrow
              >
                <div
                  style={{
                    position: 'relative',
                    height: 80,
                    background: gradientColor,
                    borderRadius: 8,
                    marginLeft: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    minWidth: '180px', // 最小幅を確保
                    padding: '12px 16px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.15)';
                  }}
                >
                  <Text size="sm" fw={700} c="white" style={{ lineHeight: 1.2 }}>
                    {cls.class_name}
                  </Text>
                  <Group gap="xs">
                    <Text size="xs" c="white" opacity={0.95} fw={500}>
                      {cls.start_time.slice(0, 5)} - {cls.end_time.slice(0, 5)}
                    </Text>
                    <Text size="xs" c="white" opacity={0.9}>
                      | {cls.instructor_name}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Badge variant="filled" color="white" c="blue" size="sm">
                      {cls.enrolled}名
                    </Badge>
                    <Badge variant="light" color="white" size="xs">
                      {cls.attendance_rate}%
                    </Badge>
                  </Group>
                </div>
              </Tooltip>
            </motion.div>
          );
        })}
      </Stack>
      </div>
    </MotionCard>
  );
};
